export interface DynamicRoadmapConfig {
  id: string;
  title: string;
  description: string;
  category: string;
  duration: number; // days
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  ageRange: string;
  goals: string[];
  dailyActivities: DailyActivity[];
  tips: string[];
  resources?: string[];
  milestones?: string[];
  userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
}

export interface DailyActivity {
  day: number;
  title: string;
  description: string;
  activities: string[];
  estimatedTime: string;
  materials?: string[];
}

export interface RoadmapGenerationRequest {
  userQuestion: string;
  conversationContext: string;
  childAge?: number;
  petAge?: number;
  petType?: 'dog' | 'cat' | 'both';
  petBreed?: string;
  preferredDuration?: number;
  specificGoals?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
}

// AI prompt templates for different types of roadmaps
export const ROADMAP_PROMPTS = {
  // For behavior/training challenges
  behavior: `Create a structured training plan for: {topic}

  Context: {context}
  Pet's age: {petAge} years
  Pet type: {petType}
  Breed: {petBreed}
  Duration: {duration} days

  Generate:
  - Clear, actionable daily training activities
  - Progressive skill building
  - Pet-friendly training methods
  - Measurable milestones
  - Age and breed-appropriate expectations`,

  // For skill development/training
  skill: `Design a skill-building training roadmap for: {topic}

  Context: {context}
  Pet's age: {petAge} years
  Pet type: {petType}
  Breed: {petBreed}
  Duration: {duration} days

  Include:
  - Daily practice training activities
  - Skill progression steps
  - Positive reinforcement methods
  - Success indicators
  - Pet owner guidance tips`,

  // For health and routine management
  routine: `Create a pet care routine roadmap for: {topic}

  Context: {context}
  Pet's age: {petAge} years
  Pet type: {petType}
  Breed: {petBreed}
  Duration: {duration} days

  Focus on:
  - Gradual habit formation
  - Consistent daily care
  - Health monitoring strategies
  - Flexibility for different breeds
  - Celebration of progress`
};

// Function to extract explicit duration from user message
function extractExplicitDuration(text: string): number | null {
  const normalizedText = text.toLowerCase();
  
  // Multi-language patterns for explicit duration
  const patterns = [
    // English patterns
    /(\d+)\s*days?/i,
    /(\d+)\s*weeks?/i,
    /(\d+)\s*months?/i,
    /only\s+(\d+)\s*days?/i,
    /just\s+(\d+)\s*days?/i,
    /i\s+have\s+(\d+)\s*days?/i,
    /(\d+)-day/i,
    /(\d+)\s*day/i,
    
    // Chinese patterns
    /(\d+)\s*天/,
    /(\d+)\s*日/,
    /(\d+)\s*周/,
    /(\d+)\s*星期/,
    /(\d+)\s*个月/,
    /只有\s*(\d+)\s*天/,
    /就\s*(\d+)\s*天/,
    /(\d+)\s*天时间/,
    
    // Spanish patterns
    /(\d+)\s*días?/i,
    /(\d+)\s*semanas?/i,
    /(\d+)\s*meses?/i,
    /solo\s+(\d+)\s*días?/i,
    /solamente\s+(\d+)\s*días?/i,
    /tengo\s+(\d+)\s*días?/i,
    
    // French patterns
    /(\d+)\s*jours?/i,
    /(\d+)\s*semaines?/i,
    /(\d+)\s*mois/i,
    /seulement\s+(\d+)\s*jours?/i,
    /j'ai\s+(\d+)\s*jours?/i,
    /(\d+)\s*jour/i
  ];
  
  for (const pattern of patterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      let duration = parseInt(match[1]);
      
      // Convert weeks to days
      if (pattern.source.includes('week') || pattern.source.includes('周') || pattern.source.includes('星期') || 
          pattern.source.includes('semana') || pattern.source.includes('semaine')) {
        duration = duration * 7;
      }
      
      // Convert months to days (approximate)
      if (pattern.source.includes('month') || pattern.source.includes('个月') || 
          pattern.source.includes('mes') || pattern.source.includes('mois')) {
        duration = duration * 30;
      }
      
      // Validate reasonable duration (1-90 days)
      if (duration >= 1 && duration <= 90) {
        console.log(`Extracted duration: ${duration} days from pattern: ${pattern.source}`);
        return duration;
      }
    }
  }
  
  return null;
}

// Function to determine roadmap type and duration based on conversation
export function analyzeConversationForRoadmap(
  userQuestion: string,
  conversationContext: string
): {
  shouldSuggestRoadmap: boolean;
  roadmapType: string;
  suggestedDuration: number;
  confidence: number;
} {
  const question = userQuestion.toLowerCase();
  const context = conversationContext.toLowerCase();
  
  console.log('Analyzing conversation for roadmap:', { question, context });
  
  // Enhanced, more flexible roadmap detection system
  const roadmapTriggers = {
    en: [
      // Direct help requests
      'how to', 'help my pet', 'help my dog', 'help my cat', 'train', 'teach', 'practice', 'improve',
      'struggling with', 'having trouble', 'want to', 'need help', 'need advice',
      'what should i do', 'what can i do', 'looking for guidance', 'seeking help',

      // Problem descriptions
      'problem with', 'issue with', 'challenge with', 'difficulty with',
      'my pet is', 'my dog is', 'my cat is', 'they are', 'he is', 'she is',

      // Behavior and training issues
      'behavior', 'training', 'obedience', 'commands', 'tricks', 'socialization',
      'aggression', 'anxiety', 'barking', 'biting', 'jumping', 'chewing',

      // Health and care concerns
      'health', 'nutrition', 'diet', 'exercise', 'grooming', 'hygiene',
      'veterinarian', 'medical', 'weight', 'energy', 'coat', 'skin',

      // Routine and daily care
      'routine', 'habit', 'schedule', 'daily', 'feeding', 'walking',
      'plan', 'program', 'roadmap', 'steps', 'guide', 'strategy',

      // Action words
      'create', 'make', 'build', 'establish', 'set up', 'organize',
      'personalized', 'customized', 'tailored', 'specific', 'detailed',
      'structured', 'systematic', 'methodical', 'organized', 'step by step'
    ],
    es: [
      // Direct help requests
      'cómo', 'ayudar a mi mascota', 'ayudar a mi perro', 'ayudar a mi gato', 'entrenar', 'enseñar', 'practicar', 'mejorar',
      'tener problemas con', 'dificultades', 'quiero', 'necesito ayuda', 'necesito consejos',
      'qué debo hacer', 'qué puedo hacer', 'buscando orientación', 'buscando ayuda',

      // Problem descriptions
      'problema con', 'problema de', 'dificultad con', 'desafío con',
      'mi mascota está', 'mi perro está', 'mi gato está', 'ellos están', 'él está', 'ella está',

      // Behavior and training issues
      'comportamiento', 'entrenamiento', 'obediencia', 'órdenes', 'trucos', 'socialización',
      'agresión', 'ansiedad', 'ladrar', 'morder', 'saltar', 'masticar',

      // Health and care concerns
      'salud', 'nutrición', 'dieta', 'ejercicio', 'aseo', 'higiene',
      'veterinario', 'médico', 'peso', 'energía', 'pelaje', 'piel',

      // Routine and daily care
      'rutina', 'hábito', 'horario', 'diario', 'alimentación', 'paseo',
      'plan', 'programa', 'mapa de ruta', 'pasos', 'guía', 'estrategia',

      // Action words
      'crear', 'hacer', 'construir', 'establecer', 'configurar', 'organizar',
      'personalizado', 'adaptado', 'específico', 'detallado',
      'estructurado', 'sistemático', 'metódico', 'organizado', 'paso a paso'
    ],
    fr: [
      // Direct help requests
      'comment', 'aider mon animal', 'aider mon chien', 'aider mon chat', 'dresser', 'enseigner', 'pratiquer', 'améliorer',
      'avoir des difficultés avec', 'problèmes', 'vouloir', 'besoin d\'aide', 'besoin de conseils',
      'que dois-je faire', 'que puis-je faire', 'chercher des conseils', 'chercher de l\'aide',

      // Problem descriptions
      'problème avec', 'problème de', 'difficulté avec', 'défi avec',
      'mon animal est', 'mon chien est', 'mon chat est', 'ils sont', 'il est', 'elle est',

      // Behavior and training issues
      'comportement', 'dressage', 'obéissance', 'commandes', 'tours', 'socialisation',
      'agressivité', 'anxiété', 'aboiement', 'morsure', 'saut', 'mâchonnement',

      // Health and care concerns
      'santé', 'nutrition', 'alimentation', 'exercice', 'toilette', 'hygiène',
      'vétérinaire', 'médical', 'poids', 'énergie', 'pelage', 'peau',

      // Routine and daily care
      'routine', 'habitude', 'horaire', 'quotidien', 'alimentation', 'promenade',
      'plan', 'programme', 'feuille de route', 'étapes', 'guide', 'stratégie',

      // Action words
      'créer', 'faire', 'construire', 'établir', 'mettre en place', 'organiser',
      'personnalisé', 'adapté', 'spécifique', 'détaillé',
      'structuré', 'systématique', 'méthodique', 'organisé', 'étape par étape'
    ],
    'zh-CN': [
      // Direct help requests
      '如何', '帮助我的宠物', '帮助我的狗', '帮助我的猫', '训练', '教', '练习', '提高',
      '在...方面有困难', '遇到问题', '想要', '需要帮助', '需要建议',
      '我应该做什么', '我能做什么', '寻找指导', '寻求帮助',

      // Problem descriptions
      '问题', '困难', '挑战', '麻烦',
      '我的宠物', '我的狗', '我的猫', '他们', '他', '她',

      // Behavior and training issues
      '行为', '训练', '服从', '命令', '技巧', '社交化',
      '攻击性', '焦虑', '吠叫', '咬人', '跳跃', '啃咬',

      // Health and care concerns
      '健康', '营养', '饮食', '运动', '美容', '卫生',
      '兽医', '医疗', '体重', '精力', '毛发', '皮肤',

      // Routine and daily care
      '习惯', '行为', '技能', '发展', '培养', '日常',
      '怎么', '怎么办', '如何做', '怎样', '方法', '建议',
      '指导', '训练', '培养', '建立', '形成', '养成',
      '计划', '安排', '程序', '路线图', '步骤', '指导',

      // Action words
      '创建', '制定', '建立', '设置', '组织',
      '个性化', '定制', '具体', '详细',
      '结构化', '系统化', '有条理', '有组织', '一步一步'
    ]
  };
  
  let shouldSuggest = false;
  let roadmapType = 'general';
  let suggestedDuration = 7;
  let confidence = 0;
  
  // Helper function to check if any language triggers match
  const checkMultiLanguageTriggers = (triggerSets: typeof roadmapTriggers, text: string) => {
    return Object.values(triggerSets).some(languageTriggers => 
      languageTriggers.some(trigger => text.includes(trigger))
    );
  };
  
  // Enhanced context analysis for better roadmap detection
  const analyzeContextForRoadmap = (question: string, context: string) => {
    let contextScore = 0;
    let contextIndicators = [];
    
    // Check for pet care context
    if (question.includes('my pet') || question.includes('my dog') || question.includes('my cat') ||
        question.includes('mi mascota') || question.includes('mi perro') || question.includes('mi gato') ||
        question.includes('mon animal') || question.includes('mon chien') || question.includes('mon chat') ||
        question.includes('我的宠物') || question.includes('我的狗') || question.includes('我的猫')) {
      contextScore += 0.3;
      contextIndicators.push('pet care context');
    }

    // Check for pet behavior issues (including breed-specific mentions)
    if (question.includes('behavior') || question.includes('training') ||
        question.includes('comportamiento') || question.includes('entrenamiento') ||
        question.includes('comportement') || question.includes('dressage') ||
        question.includes('行为') || question.includes('训练') ||
        question.includes('labrador') || question.includes('golden retriever') ||
        question.includes('german shepherd') || question.includes('persian') ||
        question.includes('siamese') || question.includes('maine coon')) {
      contextScore += 0.4;
      contextIndicators.push('pet behavior issue');
    }
    
    // Check for pet health concerns
    if (question.includes('health') || question.includes('medical') || question.includes('veterinarian') ||
        question.includes('salud') || question.includes('médico') || question.includes('veterinario') ||
        question.includes('santé') || question.includes('médical') || question.includes('vétérinaire') ||
        question.includes('健康') || question.includes('医疗') || question.includes('兽医')) {
      contextScore += 0.3;
      contextIndicators.push('pet health concern');
    }

    // Check for pet care questions
    if (question.includes('nutrition') || question.includes('diet') || question.includes('exercise') ||
        question.includes('nutrición') || question.includes('dieta') || question.includes('ejercicio') ||
        question.includes('nutrition') || question.includes('alimentation') || question.includes('exercice') ||
        question.includes('营养') || question.includes('饮食') || question.includes('运动')) {
      contextScore += 0.2;
      contextIndicators.push('pet care question');
    }
    
    // Check for specific help requests
    if (question.includes('help') || question.includes('how to') || question.includes('train') ||
        question.includes('ayuda') || question.includes('cómo') || question.includes('entrenar') ||
        question.includes('aide') || question.includes('comment') || question.includes('dresser') ||
        question.includes('帮助') || question.includes('如何') || question.includes('训练')) {
      contextScore += 0.3;
      contextIndicators.push('help request');
    }

    // Check for breed-specific situations
    if (question.includes('breed') || question.includes('labrador') || question.includes('golden') ||
        question.includes('raza') || question.includes('labrador') || question.includes('golden') ||
        question.includes('race') || question.includes('labrador') || question.includes('golden') ||
        question.includes('品种') || question.includes('拉布拉多') || question.includes('金毛')) {
      contextScore += 0.2;
      contextIndicators.push('breed-specific situation');
    }

    // Check for pet care routine context
    if (question.includes('routine') || question.includes('schedule') || question.includes('feeding') ||
        question.includes('rutina') || question.includes('horario') || question.includes('alimentación') ||
        question.includes('routine') || question.includes('horaire') || question.includes('alimentation') ||
        question.includes('习惯') || question.includes('时间表') || question.includes('喂食')) {
      contextScore += 0.2;
      contextIndicators.push('pet routine context');
    }
    
    // Check for routine/schedule mentions
    if (question.includes('routine') || question.includes('schedule') || 
        question.includes('rutina') || question.includes('horario') ||
        question.includes('routine') || question.includes('horaire') ||
        question.includes('习惯') || question.includes('时间表')) {
      contextScore += 0.2;
      contextIndicators.push('routine context');
    }
    
    return { contextScore, contextIndicators };
  };
  
  // Check if roadmap is appropriate using multi-language triggers
  const hasTriggers = checkMultiLanguageTriggers(roadmapTriggers, question) || 
                     checkMultiLanguageTriggers(roadmapTriggers, context);
  
  // Enhanced flexible keyword detection
  const roadmapActionWords = [
    'create', 'make', 'give', 'need', 'want', 'help', 'design', 'develop', 'build', 'establish',
    '制定', '创建', '给我', '需要', '想要', '帮助', '建立', '设计', '开发', '制定',
    'crear', 'hacer', 'dame', 'necesito', 'quiero', 'ayudar', 'diseñar',
    'créer', 'faire', 'donnez-moi', 'j\'ai besoin', 'aider', 'concevoir'
  ];

  const roadmapObjectWords = [
    'plan', 'roadmap', 'schedule', 'program', 'guide', 'strategy', 'approach', 'system',
    '计划', '路线图', '方案', '指南', '策略', '方法', '系统', '训练', '练习', '学习',
    'plan', 'hoja de ruta', 'programa', 'guía', 'estrategia', 'enfoque', 'entrenamiento', 'práctica',
    'plan', 'feuille de route', 'programme', 'guide', 'stratégie', 'approche', 'entraînement', 'pratique'
  ];

  const questionLower = question.toLowerCase();
  const contextLower = context.toLowerCase();

  // Check for exact phrase matches (original logic)
  const exactPhraseMatches = [
    'create a plan', 'make a plan', 'give me a plan', 'need a plan',
    'create a roadmap', 'make a roadmap', 'give me a roadmap', 'need a roadmap',
    'create a schedule', 'make a schedule', 'give me a schedule', 'need a schedule',
    'create a program', 'make a program', 'give me a program', 'need a program',
    '制定计划', '创建计划', '给我计划', '需要计划',
    '制定路线图', '创建路线图', '给我路线图', '需要路线图',
    'crear un plan', 'hacer un plan', 'dame un plan', 'necesito un plan',
    'crear una hoja de ruta', 'hacer una hoja de ruta', 'dame una hoja de ruta',
    'créer un plan', 'faire un plan', 'donnez-moi un plan', 'j\'ai besoin d\'un plan',
    'créer une feuille de route', 'faire une feuille de route', 'donnez-moi une feuille de route'
  ];

  // Additional pattern matching for more complex phrases
  const complexPhrasePatterns = [
    // Chinese patterns for complex requests
    /创建.*?计划/i,  // 创建一个...计划
    /制定.*?计划/i,  // 制定一个...计划
    /给我.*?计划/i,  // 给我一个...计划
    /需要.*?计划/i,  // 需要一个...计划
    /创建.*?路线图/i, // 创建一个...路线图
    /制定.*?路线图/i, // 制定一个...路线图
    /创建.*?方案/i,   // 创建一个...方案
    /制定.*?方案/i,   // 制定一个...方案
    // English patterns for complex requests
    /create.*?plan/i,
    /make.*?plan/i,
    /give.*?plan/i,
    /need.*?plan/i,
    /create.*?roadmap/i,
    /make.*?roadmap/i,
    /create.*?schedule/i,
    /make.*?schedule/i,
    // Spanish patterns
    /crear.*?plan/i,
    /hacer.*?plan/i,
    /dame.*?plan/i,
    /necesito.*?plan/i,
    // French patterns
    /créer.*?plan/i,
    /faire.*?plan/i,
    /donnez.*?plan/i,
    /j'ai besoin.*?plan/i
  ];

  const hasExactPhrase = exactPhraseMatches.some(phrase =>
    questionLower.includes(phrase) || contextLower.includes(phrase)
  );

  // Check for complex phrase patterns
  const hasComplexPhrase = complexPhrasePatterns.some(pattern =>
    pattern.test(question) || pattern.test(context)
  );

  // Check for flexible combinations in both question and context
  const hasActionWord = roadmapActionWords.some(word =>
    questionLower.includes(word) || contextLower.includes(word)
  );
  const hasObjectWord = roadmapObjectWords.some(word =>
    questionLower.includes(word) || contextLower.includes(word)
  );

  // More flexible detection: either exact phrase OR complex phrase OR combination of action + object
  const hasExplicitRequest = hasExactPhrase || hasComplexPhrase || (hasActionWord && hasObjectWord);
  
  // Enhanced context analysis
  const { contextScore, contextIndicators } = analyzeContextForRoadmap(question, context);
  
  console.log('Has roadmap triggers:', hasTriggers);
  console.log('Has explicit roadmap request:', hasExplicitRequest);
  console.log('Context analysis score:', contextScore);
  console.log('Context indicators:', contextIndicators);
  console.log('Question triggers found:', checkMultiLanguageTriggers(roadmapTriggers, question));
  console.log('Context triggers found:', checkMultiLanguageTriggers(roadmapTriggers, context));
  console.log('Question:', question);
  console.log('Context:', context);
  
  // Enhanced context analysis for roadmap detection
  // Check if the AI's previous response contains roadmap-related questions or suggestions
  
  // Chinese patterns
  const contextContainsRoadmapQuestion = context.includes('是否') && (
    context.includes('创建') || context.includes('计划') || context.includes('方案') || 
    context.includes('训练') || context.includes('护理') || context.includes('个性化')
  );
  
  const contextContainsRoadmapSuggestion = context.includes('建议') && (
    context.includes('创建') || context.includes('计划') || context.includes('方案') ||
    context.includes('训练') || context.includes('护理') || context.includes('个性化')
  );
  
  const contextContainsDirectQuestion = context.includes('？') && (
    context.includes('创建') || context.includes('计划') || context.includes('方案') ||
    context.includes('训练') || context.includes('护理') || context.includes('个性化')
  );
  
  // English patterns
  const contextContainsEnglishQuestion = (context.includes('would you like') || context.includes('do you want') || context.includes('should we')) && (
    context.includes('create') || context.includes('plan') || context.includes('program') ||
    context.includes('training') || context.includes('care') || context.includes('personalized')
  );
  
  const contextContainsEnglishSuggestion = (context.includes('suggest') || context.includes('recommend') || context.includes('help')) && (
    context.includes('create') || context.includes('plan') || context.includes('program') ||
    context.includes('training') || context.includes('care') || context.includes('personalized')
  );
  
  const contextContainsEnglishDirectQuestion = context.includes('?') && (
    context.includes('create') || context.includes('plan') || context.includes('program') ||
    context.includes('training') || context.includes('care') || context.includes('personalized')
  );
  
  // Spanish patterns
  const contextContainsSpanishQuestion = (context.includes('¿te gustaría') || context.includes('quieres') || context.includes('deberíamos')) && (
    context.includes('crear') || context.includes('plan') || context.includes('programa') ||
    context.includes('entrenamiento') || context.includes('cuidado') || context.includes('personalizado')
  );
  
  // French patterns
  const contextContainsFrenchQuestion = (context.includes('aimeriez-vous') || context.includes('voulez-vous') || context.includes('devrions-nous')) && (
    context.includes('créer') || context.includes('plan') || context.includes('programme') ||
    context.includes('entraînement') || context.includes('soins') || context.includes('personnalisé')
  );
  
  // Check if user's response is a positive acknowledgment to a roadmap-related question
  const isPositiveAcknowledgment = [
    // Chinese
    '好的', '行', '可以', '嗯', '嗯嗯', '是的', '对', '没错', '好的', '行', '可以', '嗯', '嗯嗯', '是的', '对', '没错',
    // English
    'ok', 'okay', 'yes', 'yeah', 'yep', 'sure', 'absolutely', 'definitely', 'of course',
    // Spanish
    'sí', 'si', 'vale', 'bueno', 'perfecto', 'claro', 'por supuesto',
    // French
    'oui', 'd\'accord', 'bien', 'parfait', 'bien sûr', 'évidemment'
  ].some(word => 
    question.toLowerCase().trim() === word.toLowerCase().trim()
  );
  
  const shouldTriggerRoadmapFromContext = isPositiveAcknowledgment && (
    contextContainsRoadmapQuestion || contextContainsRoadmapSuggestion || contextContainsDirectQuestion ||
    contextContainsEnglishQuestion || contextContainsEnglishSuggestion || contextContainsEnglishDirectQuestion ||
    contextContainsSpanishQuestion || contextContainsFrenchQuestion
  );
  
  console.log('Context contains roadmap question (Chinese):', contextContainsRoadmapQuestion);
  console.log('Context contains roadmap suggestion (Chinese):', contextContainsRoadmapSuggestion);
  console.log('Context contains direct question (Chinese):', contextContainsDirectQuestion);
  console.log('Context contains English question:', contextContainsEnglishQuestion);
  console.log('Context contains English suggestion:', contextContainsEnglishSuggestion);
  console.log('Context contains Spanish question:', contextContainsSpanishQuestion);
  console.log('Context contains French question:', contextContainsFrenchQuestion);
  console.log('Is positive acknowledgment:', isPositiveAcknowledgment);
  console.log('Should trigger roadmap from context:', shouldTriggerRoadmapFromContext);
  
  // Enhanced roadmap detection logic
  if (shouldTriggerRoadmapFromContext || (hasTriggers || hasExplicitRequest || contextScore >= 0.5)) {
    shouldSuggest = true;
    
    if (shouldTriggerRoadmapFromContext) {
      confidence += 0.8; // Highest confidence for context-based roadmap detection
    } else if (hasExplicitRequest) {
      confidence += 0.6; // High confidence for explicit requests
    } else if (contextScore >= 0.7) {
      confidence += 0.5; // High confidence for strong context indicators
    } else if (hasTriggers) {
      confidence += 0.4; // Normal confidence for trigger keywords
    } else if (contextScore >= 0.5) {
      confidence += 0.3; // Lower confidence but still suggest for moderate context
    }
    
    // Determine roadmap type using multi-language detection
    const behaviorKeywords = ['behavior', 'tantrum', 'discipline', 'problem', 'issue', 'challenge', 'comportamiento', 'rabieta', 'disciplina', 'problema', 'comportement', 'colère', 'discipline', 'problème', '行为', '发脾气', '纪律', '问题'];
    const skillKeywords = ['skill', 'learn', 'practice', 'development', 'milestone', 'habilidad', 'aprender', 'practicar', 'desarrollo', 'compétence', 'apprendre', 'pratiquer', 'développement', '技能', '学习', '练习', '发展'];
    const routineKeywords = ['routine', 'habit', 'schedule', 'daily', 'rutina', 'hábito', 'horario', 'diario', 'routine', 'habitude', 'horaire', 'quotidien', '习惯', '作息', '时间表', '日常'];
    const relationshipKeywords = ['relationship', 'sibling', 'brother', 'sister', 'friends', 'social', 'relación', 'hermanos', 'hermano', 'hermana', 'amigos', 'relation', 'frères', 'frère', 'sœur', 'amis', '关系', '兄弟姐妹', '兄弟', '姐妹', '朋友', '社交'];
    
    if (relationshipKeywords.some(keyword => question.includes(keyword) || context.includes(keyword))) {
      roadmapType = 'relationship';
      confidence += 0.2;
    } else if (behaviorKeywords.some(keyword => question.includes(keyword) || context.includes(keyword))) {
      roadmapType = 'behavior';
      confidence += 0.2;
    } else if (skillKeywords.some(keyword => question.includes(keyword) || context.includes(keyword))) {
      roadmapType = 'skill';
      confidence += 0.2;
    } else if (routineKeywords.some(keyword => question.includes(keyword) || context.includes(keyword))) {
      roadmapType = 'routine';
      confidence += 0.2;
    }
    
    // First, try to extract explicit duration from user's message
    let explicitDuration = extractExplicitDuration(question + ' ' + context);
    
    if (explicitDuration) {
      suggestedDuration = explicitDuration;
      confidence += 0.3; // High confidence when user explicitly states duration
      console.log(`Found explicit duration: ${explicitDuration} days`);
    } else {
      // Analyze task complexity and determine adaptive duration
      const difficultyLevel = assessTaskDifficulty(question, context);
      suggestedDuration = getDurationBasedOnDifficulty(difficultyLevel, question, context);
        confidence += 0.2;
      console.log(`Assessed difficulty: ${difficultyLevel}, suggested duration: ${suggestedDuration} days`);
    }
    
    // Additional confidence based on question specificity and context
    if (question.length > 50) confidence += 0.1;
    if (context.length > 100) confidence += 0.1;
    if (contextIndicators.length >= 2) confidence += 0.1; // Multiple context indicators
  }
  
  // Assess difficulty level for the roadmap
  const difficultyLevel = assessTaskDifficulty(question, conversationContext);
  
  const result = {
    shouldSuggestRoadmap: shouldSuggest && confidence > 0.4, // Lowered threshold for more flexibility
    roadmapType,
    suggestedDuration,
    difficulty: difficultyLevel,
    confidence: Math.min(1, confidence)
  };
  
  console.log('Roadmap analysis result:', result);
  return result;
}

// Assess task difficulty level based on the question and context
function assessTaskDifficulty(question: string, context: string): 'beginner' | 'intermediate' | 'advanced' {
  const combinedText = (question + ' ' + context).toLowerCase();
  
  // Advanced difficulty indicators (complex behavioral/developmental issues)
  const advancedTriggers = [
    // English
    'aggressive', 'aggression', 'hitting', 'biting', 'severe tantrums', 'meltdowns', 'autism', 'developmental delay',
    'serious behavior', 'complex needs', 'trauma', 'anxiety', 'depression', 'major transition', 'divorce',
    'multiple issues', 'ongoing problems', 'professional help', 'therapy', 'specialist',
    // Chinese
    '攻击', '打人', '咬人', '严重发脾气', '崩溃', '自闭症', '发育迟缓', '复杂需求', '创伤', '焦虑', '抑郁',
    '重大转变', '离婚', '多重问题', '持续问题', '专业帮助', '治疗', '专家',
    // Spanish  
    'agresivo', 'agresión', 'golpear', 'morder', 'berrinches severos', 'crisis', 'autismo', 'retraso del desarrollo',
    'necesidades complejas', 'trauma', 'ansiedad', 'depresión', 'transición mayor', 'divorcio', 'múltiples problemas',
    'ayuda profesional', 'terapia', 'especialista',
    // French
    'agressif', 'agression', 'frapper', 'mordre', 'crises sévères', 'effondrement', 'autisme', 'retard développemental',
    'besoins complexes', 'traumatisme', 'anxiété', 'dépression', 'transition majeure', 'divorce', 'problèmes multiples',
    'aide professionnelle', 'thérapie', 'spécialiste'
  ];
  
  // Intermediate difficulty indicators (habit formation, moderate behavioral issues)
  const intermediateTriggers = [
    // English
    'sleep problems', 'bedtime routine', 'potty training', 'toilet training', 'eating problems', 'picky eating',
    'screen time', 'technology', 'social skills', 'making friends', 'sibling rivalry', 'jealousy',
    'lying', 'dishonesty', 'defiance', 'not listening', 'tantrums', 'whining', 'transition',
    // Chinese
    '睡眠问题', '睡前例行', '如厕训练', '上厕所训练', '饮食问题', '挑食', '屏幕时间', '技术', '社交技能',
    '交朋友', '兄弟姐妹竞争', '嫉妒', '撒谎', '不诚实', '违抗', '不听话', '发脾气', '抱怨', '过渡',
    // Spanish
    'problemas de sueño', 'rutina de dormir', 'entrenamiento de baño', 'problemas de alimentación', 'comer selectivo',
    'tiempo de pantalla', 'tecnología', 'habilidades sociales', 'hacer amigos', 'rivalidad entre hermanos', 'celos',
    'mentir', 'deshonestidad', 'desafío', 'no escuchar', 'berrinches', 'quejarse', 'transición',
    // French
    'problèmes de sommeil', 'routine du coucher', 'apprentissage propreté', 'problèmes alimentation', 'manger difficile',
    'temps écran', 'technologie', 'compétences sociales', 'se faire des amis', 'rivalité fraternelle', 'jalousie',
    'mentir', 'malhonnêteté', 'défiance', 'pas écouter', 'crises', 'pleurnicher', 'transition'
  ];
  
  // Check for advanced difficulty
  if (advancedTriggers.some(trigger => combinedText.includes(trigger))) {
    return 'advanced';
  }
  
  // Check for intermediate difficulty
  if (intermediateTriggers.some(trigger => combinedText.includes(trigger))) {
    return 'intermediate';
  }
  
  // Default to beginner for basic developmental tasks
  return 'beginner';
}

// Determine duration based on difficulty level and task type
function getDurationBasedOnDifficulty(difficulty: 'beginner' | 'intermediate' | 'advanced', question: string, context: string): number {
  const combinedText = (question + ' ' + context).toLowerCase();
  
  // Check for long-term indicators that might extend duration slightly
  const longTermIndicators = [
    'habit', 'routine', 'behavior change', 'development', 'milestone', 'long-term', 'permanent',
    '习惯', '例行', '行为改变', '发展', '里程碑', '长期', '永久',
    'hábito', 'rutina', 'cambio de comportamiento', 'desarrollo', 'hito', 'largo plazo', 'permanente',
    'habitude', 'routine', 'changement comportement', 'développement', 'étape', 'long terme', 'permanent'
  ];
  
  const hasLongTermIndicators = longTermIndicators.some(indicator => combinedText.includes(indicator));
  
  switch (difficulty) {
    case 'beginner':
      // Simple tasks: 1 week baseline, extend to 10 days for habit formation (max 14 days)
      return hasLongTermIndicators ? 10 : 7;
      
    case 'intermediate': 
      // Moderate complexity: 10-12 days baseline, extend to 14 days for complex habits (max 14 days)
      return hasLongTermIndicators ? 14 : 12;
      
    case 'advanced':
      // Complex issues: always 14 days maximum regardless of complexity
      return 14;
      
    default:
      return 7; // Default fallback
  }
}

// Enhanced AI prompt for roadmap creation with conversational approach
export function generateRoadmapPrompt(request: RoadmapGenerationRequest): string {
  const { userQuestion, conversationContext, childAge, preferredDuration, specificGoals, difficulty } = request;
  
  const age = childAge || 'adult pet';
  const duration = preferredDuration || 7;
  const goals = specificGoals?.join(', ') || 'general improvement';
  const diff = difficulty || 'beginner';
  
  // Detect language and provide appropriate instructions
  const isChinese = /[\u4e00-\u9fff]/.test(userQuestion);
  const isSpanish = /[áéíóúñü]/.test(userQuestion.toLowerCase());
  const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(userQuestion.toLowerCase());
  
  // Detect if this is about pets or children based on conversation context AND user question
  const combinedText = `${conversationContext} ${userQuestion}`.toLowerCase();
  const isPetContext = /pet|dog|cat|animal|宠物|狗|猫|动物|汤圆|mascota|perro|gato|animal|animal de compagnie|chien|chat/.test(combinedText);
  
  console.log('🔍 Pet Context Detection:', {
    conversationContext,
    userQuestion,
    combinedText,
    isPetContext
  });
  
  const languageInstruction = isPetContext ? (
    isChinese ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in Chinese (中文). You MUST respond ENTIRELY in Chinese.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in Chinese (e.g., "朵朵的14天训练计划")
    - Description: MUST be in Chinese (e.g., "一个帮助您解决宠物问题的个性化训练计划")
    - Daily activities: ALL content MUST be in Chinese
    - Goals: MUST be in Chinese
    - Tips: MUST be in Chinese
    - Activity descriptions: MUST be in Chinese
    - Share prompts: MUST be in Chinese
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure
    🚨 This is a PET CARE roadmap, use appropriate Chinese pet care terminology` :
    isSpanish ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in Spanish (Español). You MUST respond ENTIRELY in Spanish.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in Spanish
    - Description: MUST be in Spanish
    - Daily activities: ALL content MUST be in Spanish
    - Goals: MUST be in Spanish
    - Tips: MUST be in Spanish
    - Activity descriptions: MUST be in Spanish
    - Share prompts: MUST be in Spanish
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure` :
    isFrench ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in French (Français). You MUST respond ENTIRELY in French.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in French
    - Description: MUST be in French
    - Daily activities: ALL content MUST be in French
    - Goals: MUST be in French
    - Tips: MUST be in French
    - Activity descriptions: MUST be in French
    - Share prompts: MUST be in French
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure` :
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in English. You MUST respond ENTIRELY in English.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in English
    - Description: MUST be in English
    - Daily activities: ALL content MUST be in English
    - Goals: MUST be in English
    - Tips: MUST be in English
    - Activity descriptions: MUST be in English
    - Share prompts: MUST be in English
    
    🚨 FORBIDDEN: NO non-English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY non-English text will cause complete failure`
  ) : (
    isChinese ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in Chinese (中文). You MUST respond ENTIRELY in Chinese.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in Chinese (e.g., "朵朵的14天训练计划")
    - Description: MUST be in Chinese (e.g., "一个帮助您解决宠物问题的个性化训练计划")
    - Daily activities: ALL content MUST be in Chinese
    - Goals: MUST be in Chinese
    - Tips: MUST be in Chinese
    - Activity descriptions: MUST be in Chinese
    - Share prompts: MUST be in Chinese
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure
    🚨 This is a PET CARE roadmap, use appropriate Chinese pet care terminology` :
    isSpanish ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in Spanish (Español). You MUST respond ENTIRELY in Spanish.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in Spanish
    - Description: MUST be in Spanish
    - Daily activities: ALL content MUST be in Spanish
    - Goals: MUST be in Spanish
    - Tips: MUST be in Spanish
    - Activity descriptions: MUST be in Spanish
    - Share prompts: MUST be in Spanish
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure` :
    isFrench ? 
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in French (Français). You MUST respond ENTIRELY in French.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in French
    - Description: MUST be in French
    - Daily activities: ALL content MUST be in French
    - Goals: MUST be in French
    - Tips: MUST be in French
    - Activity descriptions: MUST be in French
    - Share prompts: MUST be in French
    
    🚨 FORBIDDEN: NO English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY English text will cause complete failure` :
    `🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: 
    
    The pet owner asked in English. You MUST respond ENTIRELY in English.
    
    🚨 MANDATORY REQUIREMENTS:
    - Title: MUST be in English
    - Description: MUST be in English
    - Daily activities: ALL content MUST be in English
    - Goals: MUST be in English
    - Tips: MUST be in English
    - Activity descriptions: MUST be in English
    - Share prompts: MUST be in English
    
    🚨 FORBIDDEN: NO non-English text anywhere in the response
    🚨 ZERO TOLERANCE: ANY non-English text will cause complete failure`
  );
  
  // Add conversational approach context - ALWAYS use pet context for this pet care app
  const conversationalContext = isChinese ? 
    'IMPORTANT: This roadmap is being created after a collaborative conversation where we:\n1. Listened to the pet owner\'s concern\n2. Asked clarifying questions\n3. Summarized their situation\n4. Provided immediate guidance\n5. Asked for permission to create a roadmap\n\nThe pet owner has agreed to this roadmap, so make it highly personalized and directly relevant to their specific pet care needs.' :
    isSpanish ? 
    'IMPORTANTE: Este mapa de ruta se está creando después de una conversación colaborativa donde:\n1. Escuchamos la preocupación del dueño de mascota\n2. Hicimos preguntas aclaratorias\n3. Resumimos su situación\n4. Proporcionamos orientación inmediata\n5. Pedimos permiso para crear un mapa de ruta\n\nEl dueño de mascota ha aceptado este mapa de ruta, así que hazlo altamente personalizado y directamente relevante a sus necesidades específicas de cuidado de mascotas.' :
    isFrench ? 
    'IMPORTANT: Cette feuille de route est créée après une conversation collaborative où nous avons:\n1. Écouté la préoccupation du propriétaire d\'animal\n2. Posé des questions de clarification\n3. Résumé leur situation\n4. Fourni des conseils immédiats\n5. Demandé la permission de créer une feuille de route\n\nLe propriétaire d\'animal a accepté cette feuille de route, alors rendez-la hautement personnalisée et directement pertinente à leurs besoins spécifiques de soins pour animaux.' :
    'IMPORTANT: This roadmap is being created after a collaborative conversation where we:\n1. Listened to the pet owner\'s concern\n2. Asked clarifying questions\n3. Summarized their situation\n4. Provided immediate guidance\n5. Asked for permission to create a roadmap\n\nThe pet owner has agreed to this roadmap, so make it highly personalized and directly relevant to their specific pet care needs.';
  
  // ALWAYS use pet context for this pet care app
  const contextInfo = {
    subject: isChinese ? '宠物主人' : isSpanish ? 'dueño de mascota' : isFrench ? 'propriétaire d\'animal' : 'pet owner',
    target: isChinese ? '宠物' : isSpanish ? 'mascota' : isFrench ? 'animal' : 'pet',
    ageLabel: isChinese ? '宠物年龄' : isSpanish ? 'edad de la mascota' : isFrench ? 'âge de l\'animal' : 'pet\'s age',
    tipsLabel: isChinese ? '宠物主人建议' : isSpanish ? 'consejos para dueños' : isFrench ? 'conseils pour propriétaires' : 'pet owner tips',
    sharePrompt: isChinese ? '今天[活动]进行得如何？您的宠物有什么反应？请分享实施细节、挑战和成功经验！' : 
                 isSpanish ? '¿Cómo fue [actividad] hoy? ¿Cuál fue la reacción de su mascota? ¡Comparta detalles de implementación, desafíos y éxitos!' :
                 isFrench ? 'Comment s\'est passée [activité] aujourd\'hui ? Quelle a été la réaction de votre animal ? Partagez les détails d\'implémentation, défis et succès !' :
                 'How did [activity] go today? What was your pet\'s reaction? Please share implementation details, challenges, and successes!'
  };
  
  console.log('🔍 Context Info:', contextInfo);

  // Handle specific questions about what a roadmap is
  const isAskingAboutRoadmap = userQuestion.toLowerCase().includes('什么是路线图') || 
                              userQuestion.toLowerCase().includes('what is a roadmap') ||
                              userQuestion.toLowerCase().includes('qué es un roadmap') ||
                              userQuestion.toLowerCase().includes('qu\'est-ce qu\'un roadmap');
  
  const roadmapExplanation = isAskingAboutRoadmap ? (
    isChinese ? 
    'IMPORTANT: The pet owner is asking "什么是路线图？" (What is a roadmap?). This is a pet care app, so create a PET TRAINING roadmap that explains what a pet training roadmap is through a practical example. Create a roadmap that demonstrates the concept by helping with their pet\'s specific issue (based on conversation context).' :
    isSpanish ?
    'IMPORTANT: The pet owner is asking "qué es un roadmap". This is a pet care app, so create a PET TRAINING roadmap that explains what a pet training roadmap is through a practical example. Create a roadmap that demonstrates the concept by helping with their pet\'s specific issue (based on conversation context).' :
    isFrench ?
    'IMPORTANT: The pet owner is asking "qu\'est-ce qu\'un roadmap". This is a pet care app, so create a PET TRAINING roadmap that explains what a pet training roadmap is through a practical example. Create a roadmap that demonstrates the concept by helping with their pet\'s specific issue (based on conversation context).' :
    'IMPORTANT: The pet owner is asking "what is a roadmap". This is a pet care app, so create a PET TRAINING roadmap that explains what a pet training roadmap is through a practical example. Create a roadmap that demonstrates the concept by helping with their pet\'s specific issue (based on conversation context).'
  ) : '';

  return `Create a ${duration}-day learning roadmap for a ${contextInfo.subject}.

${languageInstruction}

${roadmapExplanation}

${contextInfo.subject.toUpperCase()}'S QUESTION: "${userQuestion}"
CONVERSATION CONTEXT: ${conversationContext}
${contextInfo.ageLabel.toUpperCase()}: ${age}
PLAN DURATION: ${duration} days
DIFFICULTY LEVEL: ${diff}

REQUIREMENTS:
- Respond in the user's language ONLY
- Create ${duration} unique daily activities
- Each day must include: Goal, Activity, Suggestion/Tip, and Share prompt
- Activities should build progressively toward the ${contextInfo.subject}'s goal
- Focus ONLY on activities related to their specific question

FORMAT EACH DAY AS:
第1天: [Title]
🎯 目标: [What to focus on this day]
📝 活动: [Specific, actionable activity]
💡 建议/提示: [Practical guidance]
🗣️ 与我分享: "${contextInfo.sharePrompt}"

Continue this pattern for all ${duration} days.

Include:
1. TITLE: Catchy title with emoji
2. DESCRIPTION: Brief overview
3. DAILY ACTIVITIES: ${duration} structured days
4. ${contextInfo.tipsLabel.toUpperCase()}: Practical advice
5. MILESTONES: Progress indicators

Make it age-appropriate, realistic, and directly relevant to the ${contextInfo.subject}'s question.`;
}

// Function to manage conversational flow for roadmap suggestions
export function handleConversationalRoadmapFlow(
  userQuestion: string,
  conversationContext: string,
  childAge?: number,
  userResponse?: string
): {
  nextStep: ConversationStep | null;
  shouldProceedToRoadmap: boolean;
  message: string;
} {
  // If no user response, start the conversational flow
  if (!userResponse) {
    const conversationSteps = generateConversationalRoadmapApproach(userQuestion, conversationContext, childAge);
    return {
      nextStep: conversationSteps[0], // Start with listening
      shouldProceedToRoadmap: false,
      message: conversationSteps[0].content
    };
  }
  
  // Analyze user response to determine next step
  const isChinese = /[\u4e00-\u9fff]/.test(userResponse);
  const isSpanish = /[áéíóúñü]/.test(userResponse.toLowerCase());
  const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(userResponse.toLowerCase());
  
  const language: 'zh-CN' | 'es' | 'fr' | 'en' = isChinese ? 'zh-CN' : isSpanish ? 'es' : isFrench ? 'fr' : 'en';
  
  // Check if user wants to proceed with roadmap
  const positiveResponses = {
    'zh-CN': ['好的', '可以', '是的', '愿意', '希望', '想要', '同意', '行', '没问题'],
    'es': ['sí', 'claro', 'por supuesto', 'me gustaría', 'quiero', 'está bien', 'perfecto', 'vale'],
    'fr': ['oui', 'bien sûr', 'parfait', 'je voudrais', 'je veux', 'd\'accord', 'parfait', 'ok'],
    'en': ['yes', 'sure', 'okay', 'ok', 'please', 'would like', 'want', 'agree', 'good idea']
  };
  
  const negativeResponses = {
    'zh-CN': ['不', '不用', '不需要', '算了', '暂时不用', '以后再说'],
    'es': ['no', 'no gracias', 'no necesito', 'más tarde', 'por ahora no'],
    'fr': ['non', 'non merci', 'je n\'ai pas besoin', 'plus tard', 'pas maintenant'],
    'en': ['no', 'not now', 'later', 'don\'t need', 'not interested', 'maybe later']
  };
  
  const positivePattern = new RegExp(`(${positiveResponses[language].join('|')})`, 'i');
  const negativePattern = new RegExp(`(${negativeResponses[language].join('|')})`, 'i');
  
  if (positivePattern.test(userResponse)) {
    // User agrees to roadmap
    const conversationSteps = generateConversationalRoadmapApproach(userQuestion, conversationContext, childAge);
    const draftStep = conversationSteps.find(step => step.type === 'draft_roadmap');
    
    return {
      nextStep: draftStep || null,
      shouldProceedToRoadmap: true,
      message: draftStep?.content || 'Great! Let me create a personalized roadmap for you.'
    };
  } else if (negativePattern.test(userResponse)) {
    // User declines roadmap
    const conversationSteps = generateConversationalRoadmapApproach(userQuestion, conversationContext, childAge);
    const continueStep = conversationSteps.find(step => step.type === 'continue_dialogue');
    
    return {
      nextStep: continueStep || null,
      shouldProceedToRoadmap: false,
      message: continueStep?.content || 'No problem! Let\'s continue our conversation.'
    };
  }
  
  // User provided more information, continue with clarifying questions
  const conversationSteps = generateConversationalRoadmapApproach(userQuestion, conversationContext, childAge);
  const nextStep = conversationSteps.find(step => step.type === 'clarify');
  
  return {
    nextStep: nextStep || null,
    shouldProceedToRoadmap: false,
    message: nextStep?.content || 'Thank you for sharing more details. Let me ask a few more questions to better understand your situation.'
  };
}

// Parse AI response into roadmap structure
export function parseAIResponseToRoadmap(
  aiResponse: string,
  request: RoadmapGenerationRequest
): DynamicRoadmapConfig | null {
  try {
    console.log('Parsing AI response:', aiResponse);
    
    // Check for error responses that indicate AI cannot assist
    const errorResponses = [
      '对不起，我无法协助您的请求',
      'Sorry, I cannot assist with your request',
      'I cannot help with this request',
      'I am unable to assist',
      'No puedo ayudarte con esta solicitud',
      'Je ne peux pas vous aider avec cette demande'
    ];
    
    const isErrorResponse = errorResponses.some(error => 
      aiResponse.toLowerCase().includes(error.toLowerCase())
    );
    
    if (isErrorResponse) {
      console.log('AI returned error response, cannot generate roadmap');
      return null;
    }
    
    const lines = aiResponse.split('\n').filter(line => line.trim());
    console.log('Filtered lines:', lines);
    
    // Extract title and description
    let title = `🌟 Independence Adventure: ${request.preferredDuration || 7}-Day Roadmap`;
    let description = `A ${request.preferredDuration || 7}-day plan to help with your question`;
    
    // Look for title patterns - prioritize main plan titles over daily titles
    const titlePatterns = [
      /TITLE:\s*(.+)/i,
      /Title:\s*(.+)/i,
      // Priority 1: Specific keyword patterns (language specific)
      /^([^\n]+(?:计划|路线图|培训计划|训练计划|成长路线)[^\n]*)/i, // Chinese plan titles
      /^([A-Z][^:\n]+(?:Plan|Roadmap|Journey|Adventure))/i, // Lines with key words
      /^([^\n]+(?:Roadmap|Plan|Journey|Adventure)[^\n]*)/i, // Lines containing key words
      // Priority 2: Emoji patterns
      /^#\s*([🌟🚀🎯🎨🗣️😊][^\n]+)/, // Single # header with emoji
      /^([🌟🚀🎯🎨🗣️😊][^\n]+)/, // Line starting with emoji
      // Priority 3: H1/H2 headers (Main title), explicitly excluding H3 (###) which is usually for days
      /^#{1,2}\s+([^#\n]+)/, 
      // Fallback: Generic # header (might catch day titles if they are top of file, but less likely now)
      /^#\s*([^\n]+)/, 
      // Lowest priority: Daily titles (in case nothing else matches)
      /^###\s*第\d+天[：:]\s*(.+)/, 
      /^第\d+天[：:]\s*(.+)/ 
    ];
    
    // Clean up title by removing markdown formatting and limiting length
    const cleanTitle = (title: string): string => {
      return title
        .replace(/^#+\s*/, '') // Remove markdown headers
        .replace(/^##\s*/, '') // Remove double ##
        .replace(/^###\s*/, '') // Remove triple ###
        .replace(/^####\s*/, '') // Remove quadruple ####
        .trim()
        .substring(0, 60); // Limit to 60 characters
    };
    
    for (const pattern of titlePatterns) {
      const match = lines.find(line => pattern.test(line));
      if (match) {
        const extracted = match.match(pattern);
        if (extracted && extracted[1]) {
          title = cleanTitle(extracted[1]);
          console.log('Found title:', title);
          break;
        }
      }
    }
    
    // Look for description patterns
    const descPatterns = [
      /DESCRIPTION:\s*(.+)/i,
      /Description:\s*(.+)/i,
      /overview[:\s]+(.+)/i,
      /plan will accomplish[:\s]+(.+)/i
    ];
    
    // Clean up description by limiting length
    const cleanDescription = (desc: string): string => {
      return desc
        .replace(/^#+\s*/, '') // Remove markdown headers
        .trim()
        .substring(0, 120); // Limit to 120 characters
    };
    
    for (const pattern of descPatterns) {
      const match = lines.find(line => pattern.test(line));
      if (match) {
        const extracted = match.match(pattern);
        if (extracted && extracted[1]) {
          description = cleanDescription(extracted[1]);
          console.log('Found description:', description);
          break;
        }
      }
    }
    
    // Extract daily activities by looking for day patterns
    const dailyActivities: DailyActivity[] = [];
    const maxDays = request.preferredDuration || 7;
    
    // Look for structured day content
    for (let day = 1; day <= maxDays; day++) {
      // Find the main day section header (avoiding markdown formatting and duplicates)
      const daySectionStart = lines.findIndex(line => {
        const lineLower = line.toLowerCase();
        const lineOriginal = line;
        
        // English patterns
        const englishPatterns = [
          `day ${day}:`,
          `day ${day}.`,
          `### day ${day}`,
          `**day ${day}:`
        ];
        
        // Chinese patterns - handle both Arabic and Chinese numbers
        const chinesePatterns = [
          `第${day}天`,
          `第${day}天：`,
          `第${day}天.`,
          `### 第${day}天`,
          `**第${day}天：`
        ];
        
        // Add Chinese number patterns for each day
        const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
        if (day <= chineseNumbers.length) {
          const chineseDay = chineseNumbers[day - 1];
          chinesePatterns.push(
            `第${chineseDay}天`,
            `第${chineseDay}天：`,
            `第${chineseDay}天.`,
            `### 第${chineseDay}天`,
            `**第${chineseDay}天：`
          );
        }
        
        // Spanish patterns
        const spanishPatterns = [
          `día ${day}:`,
          `día ${day}.`,
          `### día ${day}`,
          `**día ${day}:`
        ];
        
        // French patterns
        const frenchPatterns = [
          `jour ${day}:`,
          `jour ${day}.`,
          `### jour ${day}`,
          `**jour ${day}:`
        ];
        
        // Check if line matches any pattern
        const matchesPattern = [
          ...englishPatterns,
          ...chinesePatterns,
          ...spanishPatterns,
          ...frenchPatterns
        ].some(pattern => lineLower.includes(pattern) || lineOriginal.includes(pattern));
        
        // Check that it doesn't match future days
        const futureDays = [day + 1, day + 2, day + 3, day + 4, day + 5, day + 6, day + 7, day + 8, day + 9, day + 10];
        const doesntMatchFuture = futureDays.every(futureDay => {
          const chineseFutureDay = futureDay <= chineseNumbers.length ? chineseNumbers[futureDay - 1] : null;
          return !lineLower.includes(`day ${futureDay}`) &&
                 !lineOriginal.includes(`第${futureDay}天`) &&
                 !(chineseFutureDay && lineOriginal.includes(`第${chineseFutureDay}天`)) &&
                 !lineLower.includes(`día ${futureDay}`) &&
                 !lineLower.includes(`jour ${futureDay}`);
        });
        
        return matchesPattern && doesntMatchFuture;
      });
      
      if (daySectionStart !== -1) {
        // Extract day title from the main header, cleaning up markdown
        const dayHeaderLine = lines[daySectionStart];
        
        // Language detection for proper title extraction
        const isChinese = /[\u4e00-\u9fff]/.test(request.userQuestion || aiResponse);
        const isSpanish = /[áéíóúñü]/.test((request.userQuestion || aiResponse).toLowerCase());
        const isFrench = /[àâäéèêëïîôöùûüÿç]/.test((request.userQuestion || aiResponse).toLowerCase());
        
        let dayTitle = dayHeaderLine
          .replace(/^#+\s*/, '') // Remove markdown headers
          .replace(/^\*\*/, '') // Remove bold markdown
          .replace(/\*\*$/, '') // Remove bold markdown
          .trim();
        
        // Extract title based on language patterns
        if (isChinese) {
          // For Chinese patterns, just extract content after colon without adding day format
          if (dayTitle.includes('第') && dayTitle.includes('天') && dayTitle.includes('：')) {
            // Extract content after the colon (：)
            const contentAfterColon = dayTitle.split('：')[1];
            if (contentAfterColon) {
              dayTitle = contentAfterColon.trim();
            } else {
              dayTitle = dayTitle.replace(/^第[一二三四五六七八九十\d]+天[：:]\s*/, '').trim();
            }
          } else {
            // If no day format found, use as is
            dayTitle = dayTitle;
          }
        } else if (isSpanish) {
          // For Spanish patterns like "Día 1: Title"
          dayTitle = dayTitle
            .replace(/^día\s+\d+[：:]\s*/i, '') // Remove "Día X:"
            .replace(/^día\s+\d+\s*/i, '') // Remove "Día X "
            .trim() || `Día ${day}`;
        } else if (isFrench) {
          // For French patterns like "Jour 1: Title"
          dayTitle = dayTitle
            .replace(/^jour\s+\d+[：:]\s*/i, '') // Remove "Jour X:"
            .replace(/^jour\s+\d+\s*/i, '') // Remove "Jour X "
            .trim() || `Jour ${day}`;
        } else {
          // For English patterns like "Day 1: Title"
          dayTitle = dayTitle
            .replace(/^day\s+\d+[：:]\s*/i, '') // Remove "Day X:"
            .replace(/^day\s+\d+\s*/i, '') // Remove "Day X "
            .trim() || `Day ${day}`;
        }
        
        const dayDesc = isChinese ? '' :
                       isSpanish ? '' :
                       isFrench ? '' :
                       '';
        
        // Look for the new structured format: Goal, Activity, Suggestion/Tip, Share prompt
        // Increase line limit to capture full content including long share prompts
        const daySectionLines = lines.slice(daySectionStart, daySectionStart + 25);
        
        let goal = '';
        let activity = '';
        let suggestion = '';
        let sharePrompt = '';
        let activities: string[] = [];
        
        // Parse the new structure with improved multi-line handling and multi-language support
        for (let i = 0; i < daySectionLines.length; i++) {
          const line = daySectionLines[i];
          
          // Enhanced pattern matching for multiple languages
          if (line.includes('🎯') && (
            line.toLowerCase().includes('goal') || 
            line.includes('目标') || 
            line.includes('objetivo') || 
            line.includes('objectif')
          )) {
            goal = line.replace(/🎯.*?[:：]\s*/, '').trim();
          } else if (line.includes('📝') && (
            line.toLowerCase().includes('activity') || 
            line.includes('活动') || 
            line.includes('actividad') || 
            line.includes('activité')
          )) {
            activity = line.replace(/📝.*?[:：]\s*/, '').trim();
          } else if (line.includes('💡') && (
            line.toLowerCase().includes('suggestion') || 
            line.toLowerCase().includes('tip') || 
            line.includes('建议') || 
            line.includes('提示') || 
            line.includes('consejo') || 
            line.includes('conseil')
          )) {
            suggestion = line.replace(/💡.*?[:：]\s*/, '').trim();
          } else if (line.includes('🗣️') && (
            line.toLowerCase().includes('share') || 
            line.includes('分享') || 
            line.toLowerCase().includes('comparte') || 
            line.toLowerCase().includes('partage')
          )) {
            // Handle multi-line share prompts that might continue on next lines
            let fullSharePrompt = line.replace(/🗣️.*?[:：]\s*/, '').trim();
            
            // Check if the share prompt continues on subsequent lines (common for long prompts)
            let j = i + 1;
            while (j < daySectionLines.length && 
                   !daySectionLines[j].includes('🎯') && 
                   !daySectionLines[j].includes('📝') && 
                   !daySectionLines[j].includes('💡') && 
                   !daySectionLines[j].includes('🗣️') &&
                   !daySectionLines[j].toLowerCase().includes('day') &&
                   !daySectionLines[j].includes('天') && // Chinese day indicator
                   !daySectionLines[j].includes('día') && // Spanish day indicator
                   !daySectionLines[j].includes('jour') && // French day indicator
                   daySectionLines[j].trim().length > 0) {
              fullSharePrompt += ' ' + daySectionLines[j].trim();
              j++;
            }
            
            sharePrompt = fullSharePrompt;
          }
        }
        
        // If we found the new structure, use it
        if (goal && activity) {
          activities = [
            `🎯 Goal: ${goal}`,
            `📝 Activity: ${activity}`,
            `💡 Tip: ${suggestion || 'Take your time and be encouraging'}`,
            `🗣️ ${sharePrompt || 'Share your experience with me!'}`
          ];
        } else {
          // Fall back to old bullet point parsing with multi-language support
        const dayActivities = daySectionLines.filter(line => 
          (line.includes('•') || line.includes('-') || line.includes('*')) &&
          line.trim().length > 10 && // Must have actual content
          !line.toLowerCase().includes('day') && // Don't include lines that just repeat "day X"
          !line.includes('天') && // Don't include lines that just repeat "天 X" (Chinese day)
          !line.includes('día') && // Don't include lines that just repeat "día X" (Spanish day)
          !line.includes('jour') && // Don't include lines that just repeat "jour X" (French day)
          !line.toLowerCase().includes('title') && // Don't include lines that just repeat titles
          !line.includes('标题') && // Don't include lines that just repeat Chinese titles
          !line.includes('título') && // Don't include lines that just repeat Spanish titles
          !line.includes('titre') && // Don't include lines that just repeat French titles
          !line.toLowerCase().includes('####') && // Don't include markdown headers
          !line.toLowerCase().includes('###') && // Don't include markdown headers
          !line.toLowerCase().includes('##') && // Don't include markdown headers
          !line.toLowerCase().includes('#') && // Don't include markdown headers
          !line.includes('**') && // Don't include bold markdown
          !line.match(/^\*\*.*\*\*$/) && // Don't include lines that are just bold text
          !line.match(/^[A-Z][^:]*:\s*$/) && // Don't include lines that are just headers
          !line.match(/^[A-Z][^:]*\s*$/) // Don't include lines that are just titles
        );
        
        if (dayActivities.length > 0) {
          activities = dayActivities
            .slice(0, 3)
            .map(line => {
              // Remove bullet points first
              let cleaned = line.replace(/^[•\-*]\s*/, '').trim();
              
              // Remove redundant day indicators from the beginning of content
              cleaned = cleaned
                .replace(/^第[一二三四五六七八九十\d]+天[：:]\s*/, '') // Remove "第X天：" or "第X天:"
                .replace(/^第[一二三四五六七八九十\d]+天\s*/, '') // Remove "第X天 "
                .replace(/^day\s+\d+[：:]\s*/i, '') // Remove "Day X:" or "Day X："
                .replace(/^day\s+\d+\s*/i, '') // Remove "Day X "
                .replace(/^día\s+\d+[：:]\s*/i, '') // Remove "Día X:" or "Día X："
                .replace(/^día\s+\d+\s*/i, '') // Remove "Día X "
                .replace(/^jour\s+\d+[：:]\s*/i, '') // Remove "Jour X:" or "Jour X："
                .replace(/^jour\s+\d+\s*/i, '') // Remove "Jour X "
                .trim();
              
              return cleaned;
            })
            .filter(act => act.length > 0 && 
              !act.toLowerCase().includes('day') && 
              !act.includes('天') && 
              !act.includes('día') && 
              !act.includes('jour'));
          }
        }
        
        // If still no activities found, use fallback
        if (activities.length === 0) {
          // Generate structured fallback activities based on the day number
          const fallbackActivities = [
            {
              day: 1,
              activities: [
                "🎯 Goal: Build basic trust and bonding",
                "📝 Activity: Spend 10 minutes playing with your pet using their favorite toy", 
                "💡 Tip: Use positive reinforcement and be patient with your pet",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 2,
              activities: [
                "🎯 Goal: Establish feeding routine and independence",
                "📝 Activity: Let your pet choose between two different food options and eat independently",
                "💡 Tip: Use appropriate portion sizes and monitor your pet's eating habits",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 3,
              activities: [
                "🎯 Goal: Develop responsibility and organization skills",
                "📝 Activity: Ask your pet to put away 5 toys and clean their feeding area",
                "💡 Tip: Break tasks into small steps and offer praise",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 4,
              activities: [
                "🎯 Goal: Strengthen decision-making abilities",
                "📝 Activity: Let your pet choose between different treats and pick which toy to play with",
                "💡 Tip: Provide 2-3 good options to choose from",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 5,
              activities: [
                "🎯 Goal: Build confidence and problem-solving skills",
                "📝 Activity: Let your pet try a new activity alone and solve a simple puzzle",
                "💡 Tip: Stay nearby but let them work independently first",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 6,
              activities: [
                "🎯 Goal: Expand responsibility and care skills",
                "📝 Activity: Help with grooming and feeding routine",
                "💡 Tip: Show them how first, then let them take the lead",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            },
            {
              day: 7,
              activities: [
                "🎯 Goal: Integrate all independence skills",
                "📝 Activity: Complete daily routine and help with meal preparation",
                "💡 Tip: Celebrate their growth and independence journey",
                "🗣️ Share with me: Please tell me how this went! What was your pet's reaction? Share the implementation details, challenges, and successes with the assistant."
              ]
            }
          ];
          
          const fallback = fallbackActivities.find(d => d.day === day);
          if (fallback) {
            activities = fallback.activities;
          } else {
            // For days beyond 7, generate generic but different activities
            activities = [
              `Practice independence skill ${day} - activity 1`,
              `Practice independence skill ${day} - activity 2`, 
              `Practice independence skill ${day} - activity 3`
            ];
          }
        }
        
        dailyActivities.push({
          day,
          title: dayTitle,
          description: dayDesc,
          activities: activities.length > 0 ? activities : [`Activity ${day}.1`, `Activity ${day}.2`, `Activity ${day}.3`],
          estimatedTime: '15-20 minutes',
          materials: []
        });
      } else {
        // For days that don't have content, use the improved fallback system
        const fallbackActivities = [
          {
            day: 1,
            title: "Dress for Success",
            description: "Practice self-dressing skills",
            activities: ["Let your child choose between 2 shirts", "Practice putting on socks independently", "Try zipping up a jacket together"]
          },
          {
            day: 2,
            title: "Breakfast Boss", 
            description: "Make food choices and decisions",
            activities: ["Let your child pick between cereal or toast", "Have them pour their own milk", "Let them set their own place at the table"]
          },
          {
            day: 3,
            title: "Clean-up Captain",
            description: "Take responsibility for tasks",
            activities: ["Ask your child to put 5 toys away", "Let them wipe the table with a cloth", "Have them help fold small towels"]
          },
          {
            day: 4,
            title: "Choice Champion",
            description: "Practice decision-making skills",
            activities: ["Let them choose between 3 snack options", "Have them pick which book to read", "Let them decide what to wear tomorrow"]
          },
          {
            day: 5,
            title: "Confidence Builder",
            description: "Build self-reliance and confidence",
            activities: ["Let them try a new activity alone", "Have them solve a simple problem", "Let them show you something they learned"]
          },
          {
            day: 6,
            title: "Responsibility Hero",
            description: "Take on more responsibility",
            activities: ["Let them feed the pet with supervision", "Have them water 2 plants", "Let them help set the dinner table"]
          },
          {
            day: 7,
            title: "Independence Master",
            description: "Combine all skills learned",
            activities: ["Let them complete their morning routine alone", "Have them help make a simple sandwich", "Let them organize their toy shelf"]
          }
        ];
        
        // Language detection for fallback description
        const isChinese = /[\u4e00-\u9fff]/.test(request.userQuestion || aiResponse);
        const isSpanish = /[áéíóúñü]/.test((request.userQuestion || aiResponse).toLowerCase());
        const isFrench = /[àâäéèêëïîôöùûüÿç]/.test((request.userQuestion || aiResponse).toLowerCase());
        
        const fallbackDesc = isChinese ? '' :
                            isSpanish ? '' :
                            isFrench ? '' :
                            '';
        
        const fallback = fallbackActivities.find(d => d.day === day) || {
          day,
          title: `Day ${day}`,
          description: fallbackDesc,
          activities: [`Independence activity ${day} - skill building`, `Independence activity ${day} - practice`, `Independence activity ${day} - reinforcement`]
        };
        
        dailyActivities.push({
          day,
          title: fallback.title,
          description: fallback.description,
          activities: fallback.activities,
          estimatedTime: '15-20 minutes',
          materials: []
        });
      }
    }
    
    // Extract goals and tips from the AI response - Language-aware defaults
    const isChinese = /[\u4e00-\u9fff]/.test(request.userQuestion);
    const isSpanish = /[áéíóúñü]/.test(request.userQuestion.toLowerCase());
    const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(request.userQuestion.toLowerCase());
    
    let goals: string[];
    let tips: string[];
    
    if (isChinese) {
      goals = ['提高技能', '建立信心'];
      tips = ['保持一致性', '庆祝小胜利', '根据需要调整'];
    } else if (isSpanish) {
      goals = ['Mejorar habilidades', 'Construir confianza'];
      tips = ['Ser consistente', 'Celebrar pequeñas victorias', 'Ajustar según sea necesario'];
    } else if (isFrench) {
      goals = ['Améliorer les compétences', 'Construire la confiance'];
      tips = ['Être cohérent', 'Célébrer les petites victoires', 'Ajuster selon les besoins'];
    } else {
      goals = ['Improve skills', 'Build confidence'];
      tips = ['Be consistent', 'Celebrate small wins', 'Adjust as needed'];
    }
    
    // Look for goals section
    const goalLines = lines.filter(line => 
      line.toLowerCase().includes('goal') || 
      line.toLowerCase().includes('objective') ||
      line.toLowerCase().includes('aim')
    );
    
    if (goalLines.length > 0) {
      goals.length = 0; // Clear default goals
      goalLines.forEach(line => {
        const goal = line.replace(/.*?:/, '').trim();
        if (goal && goal.length > 5 && goal.length < 50) { // Limit goal length
          goals.push(goal);
        }
      });
    }
    
    // If no goals found or goals are too long, use concise defaults
    if (goals.length === 0) {
      if (isChinese) {
        goals.push('提高技能', '建立信心');
      } else if (isSpanish) {
        goals.push('Mejorar habilidades', 'Construir confianza');
      } else if (isFrench) {
        goals.push('Améliorer les compétences', 'Construire la confiance');
      } else {
        goals.push('Improve skills', 'Build confidence');
      }
    }
    
    // Look for tips section
    const tipLines = lines.filter(line => 
      line.toLowerCase().includes('tip') || 
      line.toLowerCase().includes('advice') ||
      line.toLowerCase().includes('suggestion')
    );
    
    if (tipLines.length > 0) {
      tips.length = 0; // Clear default tips
      tipLines.forEach(line => {
        const tip = line.replace(/.*?:/, '').trim();
        if (tip && tip.length > 5) {
          tips.push(tip);
        }
      });
    }
    
    // Determine user language based on detected language
    let userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en';
    if (isChinese) userLanguage = 'zh-CN';
    else if (isSpanish) userLanguage = 'es';
    else if (isFrench) userLanguage = 'fr';
    
    const result = {
      id: `dynamic-${Date.now()}`,
      title,
      description,
      category: 'dynamic',
      duration: request.preferredDuration || 7,
      difficulty: request.difficulty || 'beginner',
      ageRange: request.childAge ? `${request.childAge}-${request.childAge + 2}` : '2-6',
      goals: goals.length > 0 ? goals : (isChinese ? ['提高技能', '建立信心'] : 
                                         isSpanish ? ['Mejorar habilidades', 'Construir confianza'] :
                                         isFrench ? ['Améliorer les compétences', 'Construire la confiance'] :
                                         ['Improve skills', 'Build confidence']),
      dailyActivities,
      tips: tips.length > 0 ? tips : (isChinese ? ['保持一致性', '庆祝小胜利', '根据需要调整'] :
                                      isSpanish ? ['Ser consistente', 'Celebrar pequeñas victorias', 'Ajustar según sea necesario'] :
                                      isFrench ? ['Être cohérent', 'Célébrer les petites victoires', 'Ajuster selon les besoins'] :
                                      ['Be consistent', 'Celebrate small wins', 'Adjust as needed']),
      resources: [],
      milestones: [],
      userLanguage: userLanguage
    };
    
    console.log('Parsed roadmap result:', result);
    return result;
  } catch (error) {
    console.error('Error parsing AI response to roadmap:', error);
    return null;
  }
}

// New conversational approach for roadmap suggestions
export interface ConversationStep {
  type: 'listen' | 'clarify' | 'summarize' | 'immediate_guidance' | 'ask_permission' | 'draft_roadmap' | 'continue_dialogue';
  content: string;
  language: 'zh-CN' | 'es' | 'fr' | 'en';
}

export function generateConversationalRoadmapApproach(
  userQuestion: string,
  conversationContext: string,
  childAge?: number
): ConversationStep[] {
  // Detect language from user input
  const isChinese = /[\u4e00-\u9fff]/.test(userQuestion);
  const isSpanish = /[áéíóúñü]/.test(userQuestion.toLowerCase());
  const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(userQuestion.toLowerCase());
  
  const language: 'zh-CN' | 'es' | 'fr' | 'en' = isChinese ? 'zh-CN' : isSpanish ? 'es' : isFrench ? 'fr' : 'en';
  
  // Multi-language conversation steps
  const conversationSteps = {
    'zh-CN': {
      listen: '我理解您的情况。让我先仔细听听您的具体困扰。',
      clarify: '为了更好地帮助您，我想了解一些具体细节：',
      summarize: '让我总结一下您的情况：',
      immediate_guidance: '在制定详细计划之前，我想先给您一些即时的建议和支持：',
      ask_permission: '基于我们的对话，我认为制定一个学习计划可能会对您有帮助。您是否希望我为您创建一个结构化的学习路线图？',
      draft_roadmap: '太好了！我现在为您制定一个个性化的学习计划。',
      continue_dialogue: '好的，我们继续对话。如果您之后需要任何结构化的指导，随时告诉我。'
    },
    'es': {
      listen: 'Entiendo tu situación. Déjame escuchar primero tu preocupación específica.',
      clarify: 'Para ayudarte mejor, me gustaría entender algunos detalles específicos:',
      summarize: 'Déjame resumir tu situación:',
      immediate_guidance: 'Antes de crear un plan detallado, me gustaría darte algunos consejos y apoyo inmediato:',
      ask_permission: 'Basándome en nuestra conversación, creo que crear un plan de aprendizaje podría ser útil para ti. ¿Te gustaría que cree un mapa de ruta estructurado?',
      draft_roadmap: '¡Excelente! Ahora voy a crear un plan de aprendizaje personalizado para ti.',
      continue_dialogue: 'Perfecto, continuemos con la conversación. Si en algún momento necesitas orientación estructurada, házmelo saber.'
    },
    'fr': {
      listen: 'Je comprends votre situation. Laissez-moi d\'abord écouter votre préoccupation spécifique.',
      clarify: 'Pour mieux vous aider, j\'aimerais comprendre certains détails spécifiques :',
      summarize: 'Laissez-moi résumer votre situation :',
      immediate_guidance: 'Avant de créer un plan détaillé, j\'aimerais vous donner quelques conseils et un soutien immédiat :',
      ask_permission: 'Basé sur notre conversation, je pense qu\'un plan d\'apprentissage pourrait vous être utile. Souhaitez-vous que je crée une feuille de route structurée ?',
      draft_roadmap: 'Parfait ! Je vais maintenant créer un plan d\'apprentissage personnalisé pour vous.',
      continue_dialogue: 'Parfait, continuons la conversation. Si vous avez besoin d\'orientation structurée à tout moment, faites-le moi savoir.'
    },
    'en': {
      listen: 'I understand your situation. Let me first listen to your specific concern.',
      clarify: 'To better help you, I\'d like to understand some specific details:',
      summarize: 'Let me summarize your situation:',
      immediate_guidance: 'Before creating a detailed plan, I\'d like to give you some immediate advice and support:',
      ask_permission: 'Based on our conversation, I think creating a learning plan could be helpful for you. Would you like me to create a structured roadmap for you?',
      draft_roadmap: 'Great! I\'ll now create a personalized learning plan for you.',
      continue_dialogue: 'Perfect, let\'s continue our conversation. If you need any structured guidance later, just let me know.'
    }
  };
  
  const t = conversationSteps[language];
  
  // Generate clarifying questions based on the topic
  const clarifyingQuestions = generateClarifyingQuestions(userQuestion, language, childAge);
  
  // Generate immediate guidance based on the concern
  const immediateGuidance = generateImmediateGuidance(userQuestion, language, childAge);
  
  // Generate summary of the parent's concern
  const summary = generateSummary(userQuestion, conversationContext, language);
  
  return [
    {
      type: 'listen',
      content: t.listen,
      language
    },
    {
      type: 'clarify',
      content: `${t.clarify}\n\n${clarifyingQuestions}`,
      language
    },
    {
      type: 'summarize',
      content: `${t.summarize}\n\n${summary}`,
      language
    },
    {
      type: 'immediate_guidance',
      content: `${t.immediate_guidance}\n\n${immediateGuidance}`,
      language
    },
    {
      type: 'ask_permission',
      content: t.ask_permission,
      language
    }
  ];
}

// Generate clarifying questions based on the topic and language
function generateClarifyingQuestions(question: string, language: 'zh-CN' | 'es' | 'fr' | 'en', childAge?: number): string {
  const questions = {
    'zh-CN': {
      behavior: [
        '这种情况多久发生一次？',
        '通常在什么情况下发生？',
        '您已经尝试过哪些方法？',
        '孩子的反应如何？'
      ],
      skill: [
        '您希望孩子掌握什么具体技能？',
        '孩子目前在这个方面处于什么水平？',
        '您每天能投入多少时间？',
        '有什么特定的挑战吗？'
      ],
      routine: [
        '您希望建立什么样的日常习惯？',
        '目前家庭的时间安排是怎样的？',
        '有什么固定的时间点吗？',
        '其他家庭成员如何参与？'
      ]
    },
    'es': {
      behavior: [
        '¿Con qué frecuencia ocurre esta situación?',
        '¿En qué circunstancias suele suceder?',
        '¿Qué métodos has probado ya?',
        '¿Cómo responde tu hijo/a?'
      ],
      skill: [
        '¿Qué habilidad específica quieres que tu hijo/a desarrolle?',
        '¿En qué nivel está tu hijo/a actualmente en este aspecto?',
        '¿Cuánto tiempo puedes dedicar cada día?',
        '¿Hay algún desafío específico?'
      ],
      routine: [
        '¿Qué tipo de rutina diaria quieres establecer?',
        '¿Cómo es el horario familiar actual?',
        '¿Hay algún momento fijo del día?',
        '¿Cómo participan otros miembros de la familia?'
      ]
    },
    'fr': {
      behavior: [
        'À quelle fréquence cette situation se produit-elle ?',
        'Dans quelles circonstances cela se produit-il généralement ?',
        'Quelles méthodes avez-vous déjà essayées ?',
        'Comment votre enfant réagit-il ?'
      ],
      skill: [
        'Quelle compétence spécifique voulez-vous que votre enfant développe ?',
        'À quel niveau votre enfant se situe-t-il actuellement dans ce domaine ?',
        'Combien de temps pouvez-vous consacrer chaque jour ?',
        'Y a-t-il des défis spécifiques ?'
      ],
      routine: [
        'Quel type de routine quotidienne voulez-vous établir ?',
        'Comment est l\'horaire familial actuel ?',
        'Y a-t-il un moment fixe de la journée ?',
        'Comment les autres membres de la famille participent-ils ?'
      ]
    },
    'en': {
      behavior: [
        'How often does this situation occur?',
        'What circumstances typically trigger it?',
        'What methods have you already tried?',
        'How does your child respond?'
      ],
      skill: [
        'What specific skill do you want your child to develop?',
        'What level is your child currently at in this area?',
        'How much time can you dedicate each day?',
        'Are there any specific challenges?'
      ],
      routine: [
        'What type of daily routine do you want to establish?',
        'What is your current family schedule like?',
        'Are there any fixed times of day?',
        'How do other family members participate?'
      ]
    }
  };
  
  // Determine the category based on keywords
  let category: 'behavior' | 'skill' | 'routine' = 'behavior';
  if (/skill|learn|practice|habilidad|aprender|practicar|compétence|apprendre|pratiquer|技能|学习|练习/.test(question.toLowerCase())) {
    category = 'skill';
  } else if (/routine|habit|schedule|rutina|hábito|horario|routine|habitude|horaire|习惯|作息|时间表/.test(question.toLowerCase())) {
    category = 'routine';
  }
  
  return questions[language][category].join('\n• ');
}

// Generate immediate guidance based on the concern
function generateImmediateGuidance(question: string, language: 'zh-CN' | 'es' | 'fr' | 'en', childAge?: number): string {
  const guidance = {
    'zh-CN': {
      behavior: '• 保持冷静，深呼吸\n• 给孩子一个安全的空间\n• 用简单、清晰的语言沟通\n• 避免在情绪激动时惩罚',
      skill: '• 从简单的任务开始\n• 给予具体的表扬\n• 保持耐心和一致性\n• 创造成功的机会',
      routine: '• 从小步骤开始\n• 使用视觉提示（图片、图表）\n• 保持一致的时间\n• 庆祝小的成功'
    },
    'es': {
      behavior: '• Mantén la calma, respira profundamente\n• Dale a tu hijo/a un espacio seguro\n• Comunícate con lenguaje simple y claro\n• Evita castigos cuando estés emocionado/a',
      skill: '• Comienza con tareas simples\n• Da elogios específicos\n• Mantén la paciencia y consistencia\n• Crea oportunidades de éxito',
      routine: '• Comienza con pasos pequeños\n• Usa señales visuales (imágenes, gráficos)\n• Mantén horarios consistentes\n• Celebra los pequeños éxitos'
    },
    'fr': {
      behavior: '• Restez calme, respirez profondément\n• Donnez à votre enfant un espace sûr\n• Communiquez avec un langage simple et clair\n• Évitez les punitions quand vous êtes ému',
      skill: '• Commencez par des tâches simples\n• Donnez des compliments spécifiques\n• Maintenez la patience et la cohérence\n• Créez des opportunités de réussite',
      routine: '• Commencez par de petits pas\n• Utilisez des repères visuels (images, graphiques)\n• Maintenez des horaires cohérents\n• Célébrez les petits succès'
    },
    'en': {
      behavior: '• Stay calm, take deep breaths\n• Give your child a safe space\n• Use simple, clear language\n• Avoid punishment when emotional',
      skill: '• Start with simple tasks\n• Give specific praise\n• Maintain patience and consistency\n• Create opportunities for success',
      routine: '• Start with small steps\n• Use visual cues (pictures, charts)\n• Maintain consistent timing\n• Celebrate small successes'
    }
  };
  
  // Determine the category based on keywords
  let category: 'behavior' | 'skill' | 'routine' = 'behavior';
  if (/skill|learn|practice|habilidad|aprender|practicar|compétence|apprendre|pratiquer|技能|学习|练习/.test(question.toLowerCase())) {
    category = 'skill';
  } else if (/routine|habit|schedule|rutina|hábito|horario|routine|habitude|horaire|习惯|作息|时间表/.test(question.toLowerCase())) {
    category = 'routine';
  }
  
  return guidance[language][category];
}

// Generate summary of the parent's concern
function generateSummary(question: string, context: string, language: 'zh-CN' | 'es' | 'fr' | 'en'): string {
  const summaryTemplates = {
    'zh-CN': '您提到：{question}\n\n背景信息：{context}\n\n这听起来像是关于{category}的挑战。',
    'es': 'Mencionas: {question}\n\nContexto: {context}\n\nEsto suena como un desafío relacionado con {category}.',
    'fr': 'Vous mentionnez : {question}\n\nContexto : {context}\n\nCela semble être un défi lié à {category}.',
    'en': 'You mentioned: {question}\n\nContext: {context}\n\nThis sounds like a challenge related to {category}.'
  };

  // Determine the category based on keywords
  let category = 'behavior';
  if (/skill|learn|practice|habilidad|aprender|practicar|compétence|apprendre|pratiquer|技能|学习|练习/.test(question.toLowerCase())) {
    category = 'skill';
  } else if (/routine|habit|schedule|rutina|hábito|horario|routine|habitude|horaire|习惯|作息|时间表/.test(question.toLowerCase())) {
    category = 'routine';
  }

  const categoryNames: Record<string, Record<string, string>> = {
    'zh-CN': { behavior: '行为管理', skill: '技能发展', routine: '日常习惯' },
    'es': { behavior: 'gestión del comportamiento', skill: 'desarrollo de habilidades', routine: 'rutinas diarias' },
    'fr': { behavior: 'gestion du comportement', skill: 'développement des compétences', routine: 'routines quotidiennes' },
    'en': { behavior: 'behavior management', skill: 'skill development', routine: 'daily routines' }
  };

  return summaryTemplates[language]
    .replace('{question}', question)
    .replace('{context}', context)
    .replace('{category}', categoryNames[language][category]);
}

// Generate completion reflection prompt for the assistant
export function generateCompletionReflectionPrompt(
  roadmap: DynamicRoadmapConfig,
  userQuestion: string,
  conversationContext: string,
  userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en',
  planQaHistory?: Array<{
    day: number;
    contextType: string;
    contextElement: string;
    userQuestion: string;
    assistantResponse: string;
    timestamp: Date;
  }>
): string {
  const languageInstruction = userLanguage === 'zh-CN' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in Chinese (中文). You MUST respond ENTIRELY in Chinese. Use culturally appropriate Chinese pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    userLanguage === 'es' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in Spanish (Español). You MUST respond ENTIRELY in Spanish. Use culturally appropriate Spanish pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    userLanguage === 'fr' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in French (Français). You MUST respond ENTIRELY in French. Use culturally appropriate French pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in English. You MUST respond ENTIRELY in English. Use culturally appropriate English pet care concepts and terminology. ANY non-English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.';

  // Generate comprehensive roadmap analysis
  const roadmapGoals = roadmap.goals.join(', ');
  
  // Language-specific activity formatting
  const getDayLabel = (dayNumber: number, language: string) => {
    switch (language) {
      case 'zh-CN': return `第${dayNumber}天`;
      case 'es': return `Día ${dayNumber}`;
      case 'fr': return `Jour ${dayNumber}`;
      default: return `Day ${dayNumber}`;
    }
  };
  
  const roadmapActivities = roadmap.dailyActivities.map(day => 
    `${getDayLabel(day.day, userLanguage)}: ${day.title} - ${day.activities.slice(0, 2).join(', ')}`
  ).join('\n');

  // Generate Plan Q&A history analysis with language-specific formatting
  const getPlanQaAnalysis = (language: string) => {
    if (!planQaHistory || planQaHistory.length === 0) {
      switch (language) {
        case 'zh-CN': return '\n**Plan Q&A 互动：** 您没有使用Plan Q&A功能，但我们可以回顾整个计划。';
        case 'es': return '\n**Interacciones Plan Q&A：** No utilizaste la función Plan Q&A, pero podemos revisar todo el plan.';
        case 'fr': return '\n**Interactions Plan Q&A :** Vous n\'avez pas utilisé la fonction Plan Q&A, mais nous pouvons réviser tout le plan.';
        default: return '\n**Plan Q&A Interactions:** You didn\'t use the Plan Q&A feature, but we can review the entire plan.';
      }
    }

    const dayLabel = (day: number) => getDayLabel(day, language);
    
    switch (language) {
      case 'zh-CN':
        return `\n**Plan Q&A 互动历史分析：**
${planQaHistory.map(qa => 
  `${dayLabel(qa.day)} (${qa.contextType}): 问题 - "${qa.userQuestion}" | 回答重点 - "${qa.assistantResponse.substring(0, 100)}..."`
).join('\n')}

基于这些互动，我可以看出您在以下方面的关注点和进展：`;
      
      case 'es':
        return `\n**Análisis del historial de interacciones Plan Q&A：**
${planQaHistory.map(qa => 
  `${dayLabel(qa.day)} (${qa.contextType}): Pregunta - "${qa.userQuestion}" | Puntos clave de respuesta - "${qa.assistantResponse.substring(0, 100)}..."`
).join('\n')}

Basándome en estas interacciones, puedo ver tus áreas de enfoque y progreso en:`;
      
      case 'fr':
        return `\n**Analyse de l'historique des interactions Plan Q&A :**
${planQaHistory.map(qa => 
  `${dayLabel(qa.day)} (${qa.contextType}): Question - "${qa.userQuestion}" | Points clés de réponse - "${qa.assistantResponse.substring(0, 100)}..."`
).join('\n')}

Basé sur ces interactions, je peux voir vos domaines d'attention et progrès dans:`;
      
      default:
        return `\n**Plan Q&A Interaction History Analysis:**
${planQaHistory.map(qa => 
  `${dayLabel(qa.day)} (${qa.contextType}): Question - "${qa.userQuestion}" | Key response points - "${qa.assistantResponse.substring(0, 100)}..."`
).join('\n')}

Based on these interactions, I can see your areas of focus and progress in:`;
    }
  };

  const planQaAnalysis = getPlanQaAnalysis(userLanguage);

  const reflectionTemplates = {
    'zh-CN': `🎉 恭喜完成 "${roadmap.title}" 计划！🎉

你刚刚完成了这个精彩的学习之旅！现在让我们一起进行一个全面的回顾，分析您的进步、挑战和成就。

**计划概览：**
- **原始关注点：** "${userQuestion}"
- **计划目标：** ${roadmapGoals}
- **计划时长：** ${roadmap.duration}天
- **难度级别：** ${roadmap.difficulty}

**每日活动回顾：**
${roadmapActivities}${planQaAnalysis}

**深度反思问题：**
1. **目标达成度：** 您觉得在多大程度上实现了计划的主要目标？
2. **最有效的活动：** 哪些具体活动对您和孩子最有帮助？为什么？
3. **遇到的挑战：** 哪些部分最具挑战性？您是如何克服的？
4. **孩子的变化：** 您观察到孩子有哪些具体的进步和变化？
5. **您的成长：** 作为家长，您学到了哪些新的技巧和策略？
6. **意外收获：** 有没有超出预期的积极结果？

请详细分享您的经验。我将基于您的反馈、计划目标和我们的互动历史，为您提供专业的成就总结和个性化建议。

如果您愿意，我们也可以创建一个新的计划来继续这个旅程！

请在对话框中告诉我您的想法。`,

    'es': `🎉 ¡Felicidades por completar "${roadmap.title}"! 🎉

¡Acabas de completar este maravilloso viaje de aprendizaje! Ahora vamos a hacer una revisión completa, analizando tu progreso, desafíos y logros.

**Resumen del Plan:**
- **Preocupación inicial:** "${userQuestion}"
- **Objetivos del plan:** ${roadmapGoals}
- **Duración del plan:** ${roadmap.duration} días
- **Nivel de dificultad:** ${roadmap.difficulty}

**Revisión de actividades diarias:**
${roadmapActivities}${planQaAnalysis}

**Preguntas de reflexión profunda:**
1. **Logro de objetivos:** ¿En qué medida sientes que lograste los objetivos principales del plan?
2. **Actividades más efectivas:** ¿Qué actividades específicas fueron más útiles para ti y tu hijo/a? ¿Por qué?
3. **Desafíos encontrados:** ¿Qué partes fueron más desafiantes? ¿Cómo los superaste?
4. **Cambios en tu hijo/a:** ¿Qué progreso y cambios específicos has observado en tu hijo/a?
5. **Tu crecimiento:** Como padre/madre, ¿qué nuevas técnicas y estrategias has aprendido?
6. **Resultados inesperados:** ¿Hubo resultados positivos que no esperabas?

Por favor comparte detalladamente tu experiencia. Basándome en tu feedback, los objetivos del plan y nuestro historial de interacciones, te proporcionaré un resumen profesional de logros y consejos personalizados.

¡Si quieres, también podemos crear un nuevo plan para continuar este viaje!

Cuéntame tus pensamientos en el cuadro de conversación.`,

    'fr': `🎉 Félicitations pour avoir terminé "${roadmap.title}" ! 🎉

Vous venez de terminer ce merveilleux voyage d'apprentissage ! Maintenant, faisons une révision complète, analysant vos progrès, défis et réalisations.

**Résumé du Plan :**
- **Préoccupation initiale :** "${userQuestion}"
- **Objectifs du plan :** ${roadmapGoals}
- **Durée du plan :** ${roadmap.duration} jours
- **Niveau de difficulté :** ${roadmap.difficulty}

**Révision des activités quotidiennes :**
${roadmapActivities}${planQaAnalysis}

**Questions de réflexion approfondie :**
1. **Atteinte des objectifs :** Dans quelle mesure sentez-vous avoir atteint les objectifs principaux du plan ?
2. **Activités les plus efficaces :** Quelles activités spécifiques ont été les plus utiles pour vous et votre enfant ? Pourquoi ?
3. **Défis rencontrés :** Quelles parties ont été les plus difficiles ? Comment les avez-vous surmontés ?
4. **Changements chez votre enfant :** Quels progrès et changements spécifiques avez-vous observés chez votre enfant ?
5. **Votre croissance :** En tant que parent, quelles nouvelles techniques et stratégies avez-vous apprises ?
6. **Résultats inattendus :** Y a-t-il eu des résultats positifs que vous n'attendiez pas ?

Veuillez partager en détail votre expérience. Basé sur vos commentaires, les objectifs du plan et notre historique d'interactions, je vous fournirai un résumé professionnel des réalisations et des conseils personnalisés.

Si vous le souhaitez, nous pouvons aussi créer un nouveau plan pour continuer ce voyage !

Dites-moi vos pensées dans la boîte de conversation.`,

    'en': `🎉 Congratulations on completing "${roadmap.title}"! 🎉

You've just completed this wonderful learning journey! Now let's conduct a comprehensive review, analyzing your progress, challenges, and achievements.

**Plan Overview:**
- **Initial concern:** "${userQuestion}"
- **Plan goals:** ${roadmapGoals}
- **Plan duration:** ${roadmap.duration} days
- **Difficulty level:** ${roadmap.difficulty}

**Daily activities review:**
${roadmapActivities}${planQaAnalysis}

**Deep reflection questions:**
1. **Goal achievement:** To what extent do you feel you achieved the main goals of this plan?
2. **Most effective activities:** Which specific activities were most helpful for you and your child? Why?
3. **Challenges encountered:** Which parts were most challenging? How did you overcome them?
4. **Child's changes:** What specific progress and changes have you observed in your child?
5. **Your growth:** As a parent, what new techniques and strategies have you learned?
6. **Unexpected outcomes:** Were there any positive results you didn't anticipate?

Please share your experience in detail. Based on your feedback, the plan goals, and our interaction history, I'll provide you with a professional summary of achievements and personalized advice.

If you'd like, we can also create a new plan to continue this journey!

Please tell me your thoughts in the conversation box.`
  };

  return `Create a comprehensive completion reflection conversation for a parent who has just finished a ${roadmap.duration}-day learning roadmap.

${languageInstruction}

ORIGINAL PARENT QUESTION: "${userQuestion}"
ROADMAP COMPLETED: "${roadmap.title}"
ROADMAP DESCRIPTION: "${roadmap.description}"
ROADMAP GOALS: ${roadmapGoals}
ROADMAP ACTIVITIES: ${roadmapActivities}
CONVERSATION CONTEXT: ${conversationContext}
PLAN Q&A HISTORY: ${planQaHistory ? JSON.stringify(planQaHistory) : 'No Plan Q&A interactions'}

${reflectionTemplates[userLanguage]}

After the parent responds, you should provide a PROFESSIONAL ANALYSIS that includes:

1. **ACHIEVEMENT SUMMARY:**
   - Analyze how well they met each roadmap goal
   - Highlight specific progress indicators they mentioned
   - Compare their outcomes to the original concern

2. **CHALLENGE ANALYSIS:**
   - Acknowledge difficulties they faced
   - Provide professional insights on why certain activities were challenging
   - Celebrate their problem-solving strategies

3. **PROFESSIONAL ASSESSMENT:**
   - Evaluate their parenting growth and skill development
   - Assess their child's developmental progress
   - Identify patterns in their Plan Q&A interactions (if any)

4. **EVIDENCE-BASED RECOMMENDATIONS:**
   - Suggest specific next steps based on their feedback
   - Recommend areas for continued growth
   - Provide professional parenting advice tailored to their situation

5. **CELEBRATION & MOTIVATION:**
   - Acknowledge their dedication and effort
   - Highlight their strengths as a parent
   - Encourage continued learning and growth

IMPORTANT: This should be a professional, comprehensive analysis that demonstrates deep understanding of their journey, goals, and outcomes. Use evidence-based parenting principles and provide actionable, personalized advice.`;
}

// Generate follow-up plan suggestion prompt
export function generateFollowUpPlanSuggestionPrompt(
  roadmap: DynamicRoadmapConfig,
  userReflection: string,
  userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'
): string {
  const languageInstruction = userLanguage === 'zh-CN' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in Chinese (中文). You MUST respond ENTIRELY in Chinese. Use culturally appropriate Chinese pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    userLanguage === 'es' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in Spanish (Español). You MUST respond ENTIRELY in Spanish. Use culturally appropriate Spanish pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    userLanguage === 'fr' ?
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in French (Français). You MUST respond ENTIRELY in French. Use culturally appropriate French pet care concepts and terminology. ANY English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.' :
    '🚨🚨🚨🚨🚨 CRITICAL LANGUAGE REQUIREMENT - VIOLATION WILL RESULT IN IMMEDIATE FAILURE: The pet owner asked in English. You MUST respond ENTIRELY in English. Use culturally appropriate English pet care concepts and terminology. ANY non-English text will cause this response to fail. This is a ZERO-TOLERANCE requirement. I will reject ANY response with English text.';

  const suggestionTemplates = {
    'zh-CN': `基于您对"${roadmap.title}"计划的反馈，我为您准备了一些建议：

您的反馈：${userReflection}

我建议我们可以创建一个后续计划来继续这个重要的学习旅程。以下是一些选项：

1. **巩固现有技能** - 继续练习您已经学会的有效方法
2. **扩展到新领域** - 探索相关的其他话题和技能
3. **深入特定挑战** - 针对您提到的困难部分提供更多支持
4. **个性化调整** - 根据您的经验调整计划难度和重点

您希望我为您创建一个什么样的后续计划呢？请告诉我您的偏好，我会为您量身定制。`,

    'es': `Basándome en tus comentarios sobre "${roadmap.title}", tengo algunas sugerencias para ti:

Tus comentarios: ${userReflection}

Te sugiero que podemos crear un plan de seguimiento para continuar este importante viaje de aprendizaje. Aquí tienes algunas opciones:

1. **Consolidar habilidades existentes** - Continuar practicando los métodos efectivos que ya has aprendido
2. **Expandir a nuevas áreas** - Explorar otros temas y habilidades relacionadas
3. **Profundizar en desafíos específicos** - Proporcionar más apoyo en las partes difíciles que mencionaste
4. **Ajustes personalizados** - Adaptar la dificultad y el enfoque del plan según tu experiencia

¿Qué tipo de plan de seguimiento te gustaría que cree para ti? Por favor dime tus preferencias y lo personalizaré para ti.`,

    'fr': `Basé sur vos commentaires concernant "${roadmap.title}", j'ai quelques suggestions pour vous :

Vos commentaires : ${userReflection}

Je vous suggère que nous puissions créer un plan de suivi pour continuer ce voyage d'apprentissage important. Voici quelques options :

1. **Consolider les compétences existantes** - Continuer à pratiquer les méthodes efficaces que vous avez apprises
2. **Étendre à de nouveaux domaines** - Explorer d'autres sujets et compétences connexes
3. **Approfondir des défis spécifiques** - Fournir plus de soutien dans les parties difficiles que vous avez mentionnées
4. **Ajustements personnalisés** - Adapter la difficulté et l'accent du plan selon votre expérience

Quel type de plan de suivi aimeriez-vous que je crée pour vous ? Dites-moi vos préférences et je le personnaliserai pour vous.`,

    'en': `Based on your feedback about "${roadmap.title}", I have some suggestions for you:

Your feedback: ${userReflection}

I suggest we can create a follow-up plan to continue this important learning journey. Here are some options:

1. **Strengthen existing skills** - Continue practicing the effective methods you've already learned
2. **Expand to new areas** - Explore other related topics and skills
3. **Deepen specific challenges** - Provide more support in the difficult areas you mentioned
4. **Personalized adjustments** - Adapt the plan's difficulty and focus based on your experience

What type of follow-up plan would you like me to create for you? Please tell me your preferences and I'll customize it specifically for you.`
  };

  return `Create a follow-up plan suggestion based on the parent's reflection after completing their roadmap.

${languageInstruction}

ROADMAP COMPLETED: "${roadmap.title}"
ROADMAP DESCRIPTION: "${roadmap.description}"
PARENT'S REFLECTION: ${userReflection}

${suggestionTemplates[userLanguage]}

Based on the parent's reflection, suggest 2-3 specific follow-up plan options that would be most appropriate for their situation and interests. Keep suggestions realistic and achievable.`;
}
